import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Loader } from 'ncoded-component-library';
import { EnterpriseBusinessUnit, EnterpriseMember } from 'models/Enterprises';
import api from 'api';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import DeleteBusinessUnitModal from '../DeleteBusinessUnitModal';
import useBusinessUnits from 'hooks/useBusinessUnits';
import AddNewOption from 'components/AddNewOption';
import { FormApi } from 'final-form';
import EditOption from 'components/EditOption';
import { isSingleElementArray } from 'utils';
import EditEntepriseValueModal from 'components/EditEntepriseValueModal';
import { useTranslation } from 'react-i18next';

import './BusinessUnitsForm.styles.scss';

type BusinessUnitsFormProps = {
  className?: string;
  enterpriseId: string;
  businessUnits: EnterpriseBusinessUnit[];
  updateBusinessUnits: (
    updatedBusinessUnits: EnterpriseBusinessUnit[],
    shouldRefetch?: boolean,
  ) => void;
};

const BusinessUnitsForm: React.FC<BusinessUnitsFormProps> = (props) => {
  const {
    className,
    enterpriseId,
    businessUnits = [],
    updateBusinessUnits,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [unitId, setUnitId] = useState('');
  const [newValue, setNewValue] = useState('');
  const [oldValue, setOldValue] = useState('');
  const [
    enterpriseMembersWithBusinessUnit,
    setEnterpriseMembersWithBusinessUnit,
  ] = useState<EnterpriseMember[]>([]);

  const { popServerError } = useContext(PopNotificationsContext);

  const [editBusinessUnitModal, editBusinessUnitModalRef] =
    useCallbackRef<ModalRef>();
  const [deleteBusinessUnitModal, deleteBusinessUnitModalRef] =
    useCallbackRef<ModalRef>();

  const { sortedBusinessUnits } = useBusinessUnits(businessUnits);

  const showDelete = useMemo(
    () => !isSingleElementArray(businessUnits),
    [businessUnits],
  );

  const classes = classNames('business-units-form', className);

  const { t } = useTranslation();

  const getInitialValues = useCallback(
    (_id: string, name: string) => ({ _id, name }),
    [],
  );

  const resetStates = useCallback(() => {
    setUnitId('');
    setNewValue('');
    setOldValue('');
    setEnterpriseMembersWithBusinessUnit([]);
  }, []);

  const onAdd = useCallback(
    async ({ name }, formApi: FormApi) => {
      try {
        setIsLoading(true);
        const { data } = await api.enterprises.createBusinessUnit(
          enterpriseId,
          name,
        );
        updateBusinessUnits([...businessUnits, data], false);
        formApi.reset();
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [businessUnits, enterpriseId, popServerError, updateBusinessUnits],
  );

  const editValue = useCallback(
    async (businessUnitId: string, name: string) => {
      try {
        setIsLoading(true);
        await api.enterprises.updateBusinessUnit(businessUnitId, name);
        setEditingValue('');
        updateBusinessUnits(
          businessUnits.map((unit) =>
            unit._id === businessUnitId ? { ...unit, name } : unit,
          ),
        );
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [businessUnits, popServerError, updateBusinessUnits],
  );

  const onEdit = useCallback(
    async ({ _id, name }) => {
      try {
        setIsLoading(true);

        const { data: enterpriseMembersWithBusinessUnits } =
          await api.enterprises.getEnterpriseMembersForBusinessUnit(_id);

        if (enterpriseMembersWithBusinessUnits.length) {
          setEnterpriseMembersWithBusinessUnit(
            enterpriseMembersWithBusinessUnits,
          );
          setOldValue(businessUnits.find((bu) => bu._id === _id).name);
          setNewValue(name);
          setUnitId(_id);
          setIsLoading(false);
          editBusinessUnitModal?.open();
        } else {
          editValue(_id, name);
        }
      } catch (e) {
        popServerError(e);
      }
    },
    [businessUnits, editBusinessUnitModal, editValue, popServerError],
  );

  const onEditConfirm = useCallback(
    () => editValue(unitId, newValue),
    [editValue, newValue, unitId],
  );

  const onDelete = useCallback(
    async (_id) => {
      try {
        setIsLoading(true);

        const { data: enterpriseMembersWithBusinessUnits } =
          await api.enterprises.getEnterpriseMembersForBusinessUnit(_id);

        if (enterpriseMembersWithBusinessUnits.length) {
          setUnitId(_id);
          setEnterpriseMembersWithBusinessUnit(
            enterpriseMembersWithBusinessUnits,
          );
          deleteBusinessUnitModal?.open();
        } else {
          const { data } = await api.enterprises.deleteBusinessUnit(_id);
          updateBusinessUnits(data, false);
        }
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [deleteBusinessUnitModal, popServerError, updateBusinessUnits],
  );

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      {sortedBusinessUnits.map(({ _id, name }) => (
        <EditOption
          key={_id}
          value={name}
          isEditing={editingValue === _id}
          showDelete={showDelete}
          initialValues={getInitialValues(_id, name)}
          onPencilClick={() => setEditingValue(_id)}
          onCancel={() => setEditingValue('')}
          onDelete={() => onDelete(_id)}
          onEdit={onEdit}
        />
      ))}

      <AddNewOption onAdd={onAdd} />

      <EditEntepriseValueModal
        ref={editBusinessUnitModalRef}
        enterpriseMembersWithValue={enterpriseMembersWithBusinessUnit}
        dataType={t('businessUnit')}
        oldValue={oldValue}
        newValue={newValue}
        onClose={resetStates}
        onConfirm={onEditConfirm}
      />

      <DeleteBusinessUnitModal
        ref={deleteBusinessUnitModalRef}
        businessUnits={businessUnits}
        enterpriseBusinessUnitId={unitId}
        enterpriseMembersWithBusinessUnit={enterpriseMembersWithBusinessUnit}
        onClose={resetStates}
        updateBusinessUnits={updateBusinessUnits}
      />
    </div>
  );
};

export default BusinessUnitsForm;
